import {skipToken} from '@reduxjs/toolkit/query';
import {Card, DataStatus} from 'platform/components';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetFieldDefinitionListQuery} from '@dms/api/settings';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {EntityCustomFields, isEntityFeatureFlagEnabled} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

interface EmployeeCustomFieldsProps extends RequiredTestIdProps {
  employeeId: string;
  isEditable: boolean;
}

export function EmployeeCustomFields(props: EmployeeCustomFieldsProps) {
  const navigate = useNavigate();

  const isCustomFieldsEnabled = isEntityFeatureFlagEnabled(EntityResourceIds.employee);

  const {data: definitions, isLoading} = useGetFieldDefinitionListQuery(
    isCustomFieldsEnabled ? {resourceId: EntityResourceIds.employee, type: 'tenant'} : skipToken
  );

  if (!isCustomFieldsEnabled) {
    return null;
  }

  // Once definitions exist, EntityCustomFields renders its own titled card.
  if (isNotNilOrEmpty(definitions)) {
    return (
      <EntityCustomFields
        recordId={props.employeeId}
        resourceId={EntityResourceIds.employee}
        isEditable={props.isEditable}
        data-testid={props['data-testid']}
      />
    );
  }

  // Otherwise show the loading/empty state inside a card matching the populated one.
  return (
    <Card
      title={i18n.t('page.generalSettings.labels.customFields')}
      data-testid={suffixTestId('empty-card', props)}
    >
      <DataStatus
        isLoading={isLoading}
        isEmpty={!isLoading}
        spacing={5}
        emptyMessage={i18n.t('page.generalSettings.labels.emptyCustomFieldsMessage')}
        action={{
          variant: 'link',
          title: i18n.t('page.generalSettings.labels.emptyCustomFieldsLink'),
          onClick: () => navigate(settingsRoutes.customFields),
        }}
        data-testid={suffixTestId('empty', props)}
      />
    </Card>
  );
}
